import {
  LoginCredentials,
  StoreResponse,
  CategoryResponse,
  ProductResponse,
  UserLoginCredentials,
  UserAuthResponse,
  StoreDataResponse,
} from '../types';

const API_BASE_URL = 'https://api.restomi.fr/v3/api';

// Store information storage
let storeInfo: {
  store_id: string;
  server_token: string;
  address: string;
  location: [number, number];
  email: string;
  phone: string;
  name: string;
  image_url: string;
  country_phone_code: string;
  is_tax_included: boolean;
  item_tax: number;
  is_use_item_tax: boolean;
  general_tax: number;
  booking_fees: number;
  is_set_booking_fees: boolean;
} | null = null;

// Fonction pour journaliser les appels API
const logApiCall = (
  endpoint: string,
  request: any,
  response: any,
  status: number
) => {
  // Logs désactivés pour éviter les erreurs de quota
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json();
  logApiCall(API_BASE_URL, {}, data, response.status); // Journaliser la réponse
  if (!response.ok) {
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${
        data.status_phrase || data.message
      }`
    );
  }
  if (!data.success) {
    throw new Error(`API error: ${data.status_phrase || data.message}`);
  }
  return data;
};

export async function loginStore(
  credentials: LoginCredentials
): Promise<StoreResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/store/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await handleResponse<StoreResponse>(response);

    console.log('Réponse complète de /store/login:', data);

    if (data.success && data.store) {
      storeInfo = {
        store_id: data.store._id,
        server_token: data.store.server_token,
        address: data.store.address || '',
        location: [
          data.store.location?.[0] || 0,
          data.store.location?.[1] || 0,
        ],
        email: data.store.email || '',
        phone: data.store.phone || '',
        name: data.store.name?.[0] || '',
        image_url: data.store.image_url || '',
        country_phone_code: data.store.country_phone_code || '+33',
        is_tax_included: data.store.is_tax_included ?? false,
        item_tax: data.store.item_tax ?? 0,
        is_use_item_tax: data.store.is_use_item_tax ?? false,
        general_tax: 0,
        booking_fees: data.store.table_setting_details?.[0]?.booking_fees ?? 0,
        is_set_booking_fees: data.store.table_setting_details?.[0]?.is_set_booking_fees ?? false,
      };

      localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
      console.log('storeInfo sauvegardé après login:', storeInfo);

      try {
        const storeData = await getStoreData(
          storeInfo.store_id,
          storeInfo.server_token
        );
        if (storeData.success && storeData.store_detail) {
          storeInfo.general_tax =
            storeData.store_detail.store_taxes_details?.[0]?.tax ?? storeData.store_detail.tax_details?.[0]?.tax ?? 0;
          localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
          console.log('storeInfo mis à jour avec get_store_data:', storeInfo);
        }
      } catch (error) {
        console.warn(
          'Échec de la récupération des données du magasin après login:',
          error
        );
      }
    }

    return data;
  } catch (error) {
    console.error(
      'Login error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/login`,
      credentials,
      { error: error.message },
      500
    );
    throw error;
  }
}

export function getStoreInfo() {
  if (!storeInfo) {
    const savedInfo = localStorage.getItem('storeInfo');
    if (savedInfo) {
      try {
        const parsed = JSON.parse(savedInfo);
        if (
          parsed &&
          typeof parsed === 'object' &&
          'store_id' in parsed &&
          'server_token' in parsed &&
          'address' in parsed &&
          'location' in parsed &&
          'email' in parsed &&
          'phone' in parsed &&
          'name' in parsed &&
          'image_url' in parsed &&
          'country_phone_code' in parsed &&
          'is_tax_included' in parsed &&
          'item_tax' in parsed &&
          'is_use_item_tax' in parsed &&
          'general_tax' in parsed
        ) {
          storeInfo = parsed;
          console.log('storeInfo récupéré de localStorage:', storeInfo);
        } else {
          localStorage.removeItem('storeInfo');
          console.warn('Données invalides dans localStorage, supprimées');
        }
      } catch (e) {
        console.error('Error parsing stored store info:', e);
        localStorage.removeItem('storeInfo');
      }
    }
  }
  return storeInfo;
}

export async function logoutStore(
  storeId: string,
  serverToken: string
): Promise<void> {
  try {
    const clearStoreInfo = () => {
      storeInfo = null;
      localStorage.removeItem('storeInfo');
      console.log('storeInfo effacé');
    };

    try {
      const response = await fetch(`${API_BASE_URL}/store/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'server-token': serverToken,
        },
        body: JSON.stringify({
          store_id: storeId,
          server_token: serverToken,
        }),
      });

      await handleResponse(response);
    } catch (error) {
      console.warn(
        'Logout API call failed:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      logApiCall(
        `${API_BASE_URL}/store/logout`,
        { store_id: storeId, server_token: serverToken },
        { error: error.message },
        500
      );
    } finally {
      clearStoreInfo();
    }
  } catch (error) {
    console.error(
      'Logout process error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/logout`,
      { store_id: storeId, server_token: serverToken },
      { error: error.message },
      500
    );
    storeInfo = null;
    localStorage.removeItem('storeInfo');
  }
}

export async function getCategories(
  storeId: string,
  serverToken: string
): Promise<CategoryResponse> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const requestBody = {
      store_id: storeId,
      server_token: serverToken,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const response = await fetch(
      `${API_BASE_URL}/store/get_product_group_list`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'server-token': serverToken,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    const data = await handleResponse<CategoryResponse>(response);
    if (!data.product_groups || !Array.isArray(data.product_groups)) {
      throw new Error('Invalid categories data structure');
    }
    return data;
  } catch (error) {
    console.error(
      'Get categories error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/get_product_group_list`,
      { store_id: storeId, server_token: serverToken },
      { error: error.message },
      500
    );
    throw error;
  }
}

export async function getProducts(
  storeId: string,
  serverToken: string,
  productId: string
): Promise<ProductResponse> {
  try {
    const requestBody = {
      store_id: storeId,
      server_token: serverToken,
      timezone: 'Europe/Paris',
      product_id: productId,
    };

    const response = await fetch(
      `${API_BASE_URL}/store/get_store_product_item_list`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'server-token': serverToken,
        },
        body: JSON.stringify(requestBody),
      }
    );

    const data = await handleResponse<ProductResponse>(response);
    if (!data.products || !Array.isArray(data.products)) {
      throw new Error('Invalid products data structure');
    }
    return data;
  } catch (error) {
    console.error(
      'Get products error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/get_store_product_item_list`,
      { store_id: storeId, server_token: serverToken, product_id: productId },
      { error: error.message },
      500
    );
    throw error;
  }
}

export async function loginUser(
  credentials: UserLoginCredentials
): Promise<UserAuthResponse> {
  try {
    const requestBody = {
      email: credentials.email,
      password: credentials.password,
      login_by: credentials.login_by,
      social_id: credentials.social_id,
      device_token: credentials.device_token,
      device_type: credentials.device_type,
      app_version: credentials.app_version,
      store_type: credentials.store_type,
    };

    const response = await fetch(`${API_BASE_URL}/user/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    return await handleResponse<UserAuthResponse>(response);
  } catch (error) {
    console.error(
      'User login error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/user/login`,
      credentials,
      { error: error.message },
      500
    );
    throw error;
  }
}

export async function getStoreData(
  storeId: string,
  serverToken: string
): Promise<StoreDataResponse> {
  try {
    const requestBody = {
      store_id: storeId,
      servertoken: serverToken,
    };

    const response = await fetch(`${API_BASE_URL}/store/get_store_data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'server-token': serverToken,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await handleResponse<StoreDataResponse>(response);

    if (data.success && data.store_detail) {
      const existingStoreInfo = getStoreInfo() || {};
      storeInfo = {
        ...existingStoreInfo,
        store_id: data.store_detail._id,
        server_token: data.store_detail.server_token,
        address: data.store_detail.address || '',
        location: [
          data.store_detail.location?.[0] || 0,
          data.store_detail.location?.[1] || 0,
        ],
        email: data.store_detail.email || '',
        phone: data.store_detail.phone || '',
        name: data.store_detail.name?.[0] || '',
        image_url: data.store_detail.image_url || '',
        country_phone_code: data.store_detail.country_phone_code || '+33',
        is_tax_included: data.store_detail.is_tax_included ?? false,
        item_tax: data.store_detail.item_tax ?? 0,
        is_use_item_tax: data.store_detail.is_use_item_tax ?? false,
        general_tax: data.store_detail.store_taxes_details?.[0]?.tax ?? data.store_detail.tax_details?.[0]?.tax ?? 0,
        booking_fees: data.store_detail.table_setting_details?.[0]?.booking_fees ?? 0,
        is_set_booking_fees: data.store_detail.table_setting_details?.[0]?.is_set_booking_fees ?? false,
      };
      localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
      console.log('storeInfo mis à jour avec get_store_data:', storeInfo);
    }

    return data;
  } catch (error) {
    console.error(
      'Get store data error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/get_store_data`,
      { store_id: storeId, servertoken: serverToken },
      { error: error.message },
      500
    );
    throw error;
  }
}
